import { isValidPhoneNumber } from 'libphonenumber-js';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type { ICompany } from 'src/ts/interfaces';
import ButtonLink, {
  ButtonLinkColor,
  ButtonLinkSize,
  ButtonLinkTarget,
  ButtonLinkVariant
} from 'src/components/atoms/ButtonLink';
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { Icon } from 'src/components/atoms/Icon';
import { InputText } from 'src/components/atoms/InputText';
import { InputTextType } from 'src/components/atoms/InputText/InputText';
import { Row } from 'src/components/atoms/Row';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { AutoSuggest } from 'src/components/molecules/AutoSuggest';
import { Dropdown } from 'src/components/molecules/Dropdown';
import { FileInputField } from 'src/components/molecules/FileInputField';
import { STATES } from 'src/constants/states';
import useGooglePlacesAutocomplete from 'src/features/address/hooks/useGooglePlacesAutocomplete';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import { CompanyDetailsFormInputs } from 'src/features/company/form/ts/interfaces';
import { useSalesforceIdField } from 'src/features/company/hooks/useSalesforceIdField';
import { isValidSalesforceId, onlyRequestedSymbols } from 'src/helpers';
import { IValueLabel } from 'src/ts/interfaces';

export type CompanyDetailsFormProps = {
  company?: ICompany;
  handleOnSubmit: (data: ICompany) => void;
  isSaving?: boolean;
  settingsLink?: string | null;
};

const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = (props: CompanyDetailsFormProps) => {
  const { company, isSaving, handleOnSubmit, settingsLink } = props;

  const [isEditable, setIsEditable] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const {
    placeSuggestions,
    isLoading: isPlacesLoading,
    error: placesError,
    formatPlacesSuggestions,
    setSelectedPlace,
    selectedPlace
  } = useGooglePlacesAutocomplete({
    inputValue: searchAddress
  });

  const { isAdmin } = useUserRoles();
  const form = useForm<CompanyDetailsFormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      company: {
        name: company?.name || '',
        address: company?.address || '',
        city: company?.city || '',
        state: company?.state || '',
        zip: company?.zip || '',
        phone: company?.phone || '',
        website: company?.website || '',
        salesforce_account_id: company?.salesforce_account_id ?? '',
        logo: company?.logo
      }
    }
  });

  const { getValues, setValue } = form;
  useEffect(() => {
    if (selectedPlace?.address) {
      form.setValue('company.address', selectedPlace?.address.street, { shouldDirty: true });
      form.setValue('company.city', selectedPlace?.address.city, { shouldDirty: true });
      form.setValue('company.state', selectedPlace?.address.state, { shouldDirty: true });
      form.setValue('company.zip', selectedPlace?.address.zipCode, { shouldDirty: true });
      form.trigger('company.address');
      form.trigger('company.city');
      form.trigger('company.state');
      form.trigger('company.zip');
    }
  }, [selectedPlace, form]);

  const {
    reset,
    formState: { errors },
    handleSubmit,
    control,
    setError,
    clearErrors
  } = form;

  // Salesforce Account ID validation and availability checking
  const { indicator: salesforceIdIndicator, isLoading: isSalesforceAccountIdCheckLoading } =
    useSalesforceIdField({
      control,
      fieldName: 'company.salesforce_account_id',
      setError,
      clearErrors,
      originalValue: company?.salesforce_account_id,
      enabled: true
    });

  const onCancel = () => {
    setIsEditable(false);
    reset();
  };

  const onSubmit = (data: CompanyDetailsFormInputs) => {
    if (!data.company) return; // Form validation ensures company data exists
    handleOnSubmit(data.company);
  };

  // Early return after all hooks are called
  if (!company) return null;

  return (
    <FormProvider {...form}>
      <Row>
        <Row.Col size={1}>
          <Text color={TextColor.initial} variant={TextVariant.h2}>
            Company Details
          </Text>
        </Row.Col>
        <Row.Col size={0.75}>
          <Row gap={0.5} justify="flex-end">
            {settingsLink && (
              <ButtonLink
                name="company-settings-link"
                label="Company Settings"
                to={settingsLink}
                isExternal
                target={ButtonLinkTarget.blank}
                variant={ButtonLinkVariant.outline}
                color={ButtonLinkColor.primary}
                size={ButtonLinkSize.normal}
              >
                Company Settings
              </ButtonLink>
            )}
            <Button
              name={'edit-btn'}
              color={ButtonColor.primary}
              variant={ButtonVariant.contained}
              size={ButtonSize.normal}
              onClick={() => setIsEditable(!isEditable)}
            >
              Edit&nbsp;&nbsp;
              <Icon icon="edit" />
            </Button>
          </Row>
        </Row.Col>
      </Row>

      <Gap height={2} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <>
          <Row gap={2}>
            <Row.Col size={1}>
              <Gap height={1} />

              <Row>
                <Row.Col size={1}>
                  <InputText
                    placeholder="Company Name"
                    name="company.name"
                    defaultValue={company.name}
                    type={InputTextType.text}
                    isReadonly={!isEditable || !isAdmin}
                    config={{
                      required: 'Complete this field',
                      validate: {
                        noSpecialCharacters: (value) => onlyRequestedSymbols(value)
                      },
                      maxLength: { value: 200, message: 'Company name too long' }
                    }}
                  />
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <Controller
                    name="company.address"
                    control={form.control}
                    render={({ field: { name } }) => (
                      <AutoSuggest
                        name={name}
                        data={
                          getValues('company.address') && placeSuggestions.length === 0
                            ? [
                                {
                                  data: getValues('company.address'),
                                  value: getValues('company.address'),
                                  label: getValues('company.address')
                                } as IValueLabel
                              ]
                            : formatPlacesSuggestions(placeSuggestions)
                        }
                        placeholder="Street Address"
                        initialLabel={company.address}
                        onChange={({ value }) => {
                          setSearchAddress(value.toString());
                        }}
                        onSelect={(item) => {
                          setValue('company.address', item.value);
                          setSelectedPlace({ placeId: item?.metadata?.placeId as string });
                        }}
                        onClear={() => {
                          setValue('company.address', '');
                          setValue('company.city', '');
                          setValue('company.state', '');
                          setValue('company.zip', '');
                          setSearchAddress('');
                        }}
                        isLoading={isPlacesLoading}
                        hasError={placesError !== null}
                        config={{
                          required: 'Complete this field'
                        }}
                        setLabelToValue
                        isReadonly={!isEditable}
                        isDisabled={!isEditable}
                      />
                    )}
                  />
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <InputText
                    placeholder="City"
                    name="company.city"
                    defaultValue={company.city}
                    type={InputTextType.text}
                    isReadonly={!isEditable}
                    config={{
                      required: 'Complete this field',
                      maxLength: 50
                    }}
                  />
                </Row.Col>
                <Row.Col size={1}>
                  {isEditable ? (
                    <Controller
                      name="company.state"
                      control={control}
                      defaultValue={company.state}
                      rules={{ required: 'Complete this field' }}
                      render={({ field: { name, value, onChange } }) => (
                        <Dropdown
                          valueInitial={value}
                          data={[...STATES]}
                          onChange={({ [name]: selectedOption }) => {
                            onChange(selectedOption.selected.value);
                          }}
                          name="company.state"
                          placeholder="State"
                          size="md"
                        />
                      )}
                    />
                  ) : (
                    <InputText
                      placeholder="State"
                      name="company.state"
                      defaultValue={company.state}
                      type={InputTextType.text}
                      isReadonly={!isEditable}
                    />
                  )}
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <InputText
                    placeholder="ZIP Code"
                    name="company.zip"
                    defaultValue={company.zip}
                    type={InputTextType.text}
                    isReadonly={!isEditable}
                    config={{
                      required: 'Complete this field',
                      validate: {
                        validZip: (zip) =>
                          zip && !/^\d{5}$/.test(zip) ? 'Invalid ZIP code format' : undefined
                      }
                    }}
                  />
                </Row.Col>
                <Row.Col size={1}>
                  <InputText
                    placeholder={'Phone number'}
                    name="company.phone"
                    defaultValue={company.phone}
                    type={InputTextType.text}
                    isReadonly={!isEditable}
                    config={{
                      validate: {
                        validPhoneNumber: (value) =>
                          value && !isValidPhoneNumber(value, 'US')
                            ? 'Invalid phone number'
                            : undefined
                      }
                    }}
                  />
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <InputText
                    placeholder="Website"
                    name="company.website"
                    defaultValue={company.website}
                    type={InputTextType.text}
                    isReadonly={!isEditable}
                  />
                </Row.Col>
                <Row.Col size={1}>
                  <InputText
                    placeholder="Salesforce Account ID"
                    name="company.salesforce_account_id"
                    defaultValue={company.salesforce_account_id}
                    type={InputTextType.text}
                    isReadonly={!isEditable}
                    config={{
                      required: 'Complete this field',
                      maxLength: 18,
                      validate: {
                        validSalesforceId: (value) =>
                          isValidSalesforceId(value) ||
                          'Must be a valid 15 or 18 character Salesforce ID'
                      }
                    }}
                    endAndorment={salesforceIdIndicator}
                  />
                </Row.Col>
              </Row>

              {isEditable && (
                <Row gap={1}>
                  <Row.Col>
                    <Button
                      name="SubmitInvitationDetails"
                      variant={ButtonVariant.contained}
                      color={ButtonColor.primary}
                      size={ButtonSize.medium}
                      isLoading={isSaving}
                      isDisabled={
                        isSaving ||
                        Boolean(Object.keys(errors || {}).length) ||
                        isSalesforceAccountIdCheckLoading
                      }
                      type={ButtonType.submit}
                    >
                      {isSaving ? 'Saving' : 'Save'}
                    </Button>
                  </Row.Col>
                  <Row.Col>
                    <Button
                      name="SubmitInvitationDetails"
                      variant={ButtonVariant.outline}
                      color={ButtonColor.primary}
                      size={ButtonSize.medium}
                      type={ButtonType.button}
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                  </Row.Col>
                </Row>
              )}
            </Row.Col>
            <Row.Col alignContent="center" size={0.5}>
              <FileInputField isDisabled={!isEditable} name="company.logo" title="Company Logo" />
            </Row.Col>
          </Row>
        </>
      </form>
    </FormProvider>
  );
};

export default CompanyDetailsForm;
